import theme from "@shared/utils/themeColors";
import React from "react";
import LegalPageShell from "../components/LegalPageShell";

const sectionTitle = `text-xl md:text-2xl font-semibold mt-8 mb-4 ${theme.text.primary}`;
const subTitle = `text-lg font-semibold mt-6 mb-3 ${theme.text.primary}`;
const paragraph = `mb-4 leading-relaxed ${theme.text.secondary}`;
const listStyle = `list-disc pl-6 mb-4 space-y-1 ${theme.text.secondary}`;
const link = `${theme.text.link} hover:underline`;

const TermsConditionsPage: React.FC = () => {
  return (
    <LegalPageShell title="Terms and Conditions">
      {/* Meta */}
      <p className={`text-sm mb-1 font-semibold ${theme.text.brand}`}>
        Global Colon Cancer Association (GCCA)
      </p>
      <p className={`text-sm mb-1 ${theme.text.secondary}`}>Membership Hub</p>
      <p className={`text-xs mb-8 ${theme.text.muted}`}>
        Effective Date: 3/13/2026 &middot; Last Updated: 3/13/2026
      </p>

      {/* 1 */}
      <h2 className={sectionTitle}>1. Acceptance of Terms</h2>
      <p className={paragraph}>
        By accessing or using the Global Colon Cancer Association (GCCA)
        Membership Hub (the "Platform"), you agree to be bound by these Terms
        and Conditions ("Terms"). If you do not agree with any part of these
        Terms, you must not use the Platform. These Terms apply to all visitors,
        registered members, and others who access or use the Platform.
      </p>

      {/* 2 */}
      <h2 className={sectionTitle}>2. About the Platform</h2>
      <p className={paragraph}>
        The GCCA Membership Hub is an online platform provided by the Global
        Colon Cancer Association ("GCCA," "we," "us," or "our") that offers
        members access to:
      </p>
      <ul className={listStyle}>
        <li>Community discussion forums</li>
        <li>Educational resources and content library</li>
        <li>Event registration and management</li>
        <li>Member communications and updates</li>
      </ul>

      {/* 3 */}
      <h2 className={sectionTitle}>3. Eligibility and Registration</h2>

      <h3 className={subTitle}>3.1 Eligibility</h3>
      <p className={paragraph}>
        The Platform is intended for individuals who are at least 18 years of
        age. By registering, you confirm that you meet this age requirement.
        Individuals under 18 may use the Platform only with verifiable parental
        or guardian consent.
      </p>

      <h3 className={subTitle}>3.2 Account Creation</h3>
      <p className={paragraph}>
        To access certain features of the Platform, you must register for an
        account. You agree to provide accurate, current, and complete
        information during registration and to update such information as
        necessary. You are responsible for maintaining the confidentiality of
        your login credentials and for all activity that occurs under your
        account.
      </p>

      <h3 className={subTitle}>3.3 Account Termination</h3>
      <p className={paragraph}>
        GCCA reserves the right to suspend or terminate your account at any
        time, with or without notice, if we believe you have violated these
        Terms or if your account poses a risk to the Platform or other members.
      </p>

      {/* 4 */}
      <h2 className={sectionTitle}>4. Community Forum Rules</h2>
      <p className={paragraph}>
        When participating in discussion forums or any community areas, you
        agree to:
      </p>
      <ul className={listStyle}>
        <li>Treat all members with respect and dignity</li>
        <li>
          Not post content that is false, misleading, defamatory, obscene, or
          offensive
        </li>
        <li>
          Not share personal health information of third parties without their
          consent
        </li>
        <li>
          Not engage in spam, solicitation, or unauthorized advertising
        </li>
        <li>Not impersonate any person or entity</li>
        <li>
          Not post content that infringes any third party's intellectual
          property rights
        </li>
      </ul>
      <p className={paragraph}>
        GCCA reserves the right to remove content or restrict access to any user
        who violates these community standards, without prior notice.
      </p>

      {/* 5 */}
      <h2 className={sectionTitle}>
        5. Content and Intellectual Property
      </h2>

      <h3 className={subTitle}>5.1 GCCA Content</h3>
      <p className={paragraph}>
        All content on the Platform, including but not limited to text, graphics,
        logos, and educational materials, is the property of GCCA or its
        licensors and is protected by applicable copyright and intellectual
        property laws. You may not reproduce, distribute, modify, or create
        derivative works from any GCCA content without prior written permission.
      </p>

      <h3 className={subTitle}>5.2 User-Generated Content</h3>
      <p className={paragraph}>
        By posting content (including forum posts or event comments) on the
        Platform, you grant GCCA a non-exclusive, royalty-free, worldwide
        license to use, display, and distribute that content in connection with
        the Platform. You represent that you have all rights necessary to grant
        this license. You remain responsible for any content you post.
      </p>

      {/* 6 */}
      <h2 className={sectionTitle}>6. Event Registration</h2>
      <p className={paragraph}>
        When you register for a GCCA event through the Platform, the following
        conditions apply:
      </p>
      <ul className={listStyle}>
        <li>
          Registrations are subject to availability and any event-specific
          requirements
        </li>
        <li>
          GCCA reserves the right to cancel, postpone, or modify events;
          registered attendees will be notified as soon as practicable
        </li>
        <li>
          Any refund or transfer policies will be stated at the time of
          registration for each event
        </li>
        <li>
          By registering, you consent to being photographed or recorded at
          events for GCCA's organizational and promotional purposes, unless you
          opt out in writing prior to the event
        </li>
      </ul>

      {/* 7 */}
      <h2 className={sectionTitle}>7. Disclaimer of Warranties</h2>
      <p className={`mb-4 leading-relaxed uppercase text-sm ${theme.text.secondary}`}>
        The Platform and all content are provided "as is" and "as available"
        without warranties of any kind, express or implied. GCCA does not
        warrant that the Platform will be uninterrupted, error-free, or free of
        viruses or other harmful components. The information provided on the
        Platform is for educational and informational purposes only and is not a
        substitute for professional medical advice, diagnosis, or treatment.
      </p>

      {/* 8 */}
      <h2 className={sectionTitle}>8. Limitation of Liability</h2>
      <p className={`mb-4 leading-relaxed uppercase text-sm ${theme.text.secondary}`}>
        To the fullest extent permitted by applicable law, GCCA, its officers,
        directors, employees, and agents shall not be liable for any indirect,
        incidental, special, consequential, or punitive damages arising out of
        or related to your use of the Platform, even if GCCA has been advised of
        the possibility of such damages. In no event shall GCCA's total
        liability exceed the amount, if any, paid by you to GCCA in the twelve
        months preceding the claim.
      </p>

      {/* 9 */}
      <h2 className={sectionTitle}>
        9. Third-Party Links and Services
      </h2>
      <p className={paragraph}>
        The Platform may contain links to third-party websites or services. GCCA
        is not responsible for the content, privacy practices, or terms of any
        third-party sites. Accessing third-party sites is at your own risk. We
        encourage you to review the privacy policies of any external sites you
        visit.
      </p>

      {/* 10 */}
      <h2 className={sectionTitle}>
        10. Governing Law and Dispute Resolution
      </h2>
      <p className={paragraph}>
        These Terms shall be governed by and construed in accordance with the
        laws of the jurisdiction in which GCCA is incorporated, without regard
        to its conflict-of-law provisions. Any disputes arising under these
        Terms shall first be attempted to be resolved through good-faith
        negotiation. If unresolved, disputes may be submitted to binding
        arbitration or a court of competent jurisdiction, as required by
        applicable law.
      </p>

      {/* 11 */}
      <h2 className={sectionTitle}>11. Changes to These Terms</h2>
      <p className={paragraph}>
        GCCA reserves the right to modify these Terms at any time. We will
        notify registered members of material changes via email or a prominent
        notice on the Platform. Your continued use of the Platform after such
        notice constitutes your acceptance of the revised Terms.
      </p>

      {/* 12 */}
      <h2 className={sectionTitle}>12. Contact Us</h2>
      <p className={paragraph}>
        If you have questions about these Terms and Conditions, please contact
        us at:
      </p>
      <p className={`mb-1 font-semibold ${theme.text.primary}`}>
        Global Colon Cancer Association
      </p>
      <p className={paragraph}>
        Email:{" "}
        <a href="mailto:info@globalcca.org" className={link}>
          info@globalcca.org
        </a>
      </p>
      <p className={paragraph}>
        Website:{" "}
        <a
          href="https://www.globalcca.org"
          target="_blank"
          rel="noopener noreferrer"
          className={link}
        >
          GLOBALCCA.ORG
        </a>
      </p>
      <p className={paragraph}>
        Mailing Address: 5335 Wisconsin Ave NW, Suite 440, Washington, DC 20015
        USA
      </p>
    </LegalPageShell>
  );
};

export default TermsConditionsPage;
